import { gql } from '@apollo/client';
import { CohortConfigFragment } from '../cohorts';
import {
  DatePickerFieldsFragment,
  LogicalOperatorFragment,
  MeasureFunctionFragment,
  PartitionFieldsFragment,
  PartitionValueFieldsFragment,
  RelationalOperatorFragment,
  SubjectFragment,
} from '../fragments';
import {
  AttributionFragment,
  DateKindSupportFragment,
  ExpressionModeFieldsFragment,
  FunnelModeFieldsFragment,
  GroupbyFragment,
  RetentionCalculationFragment,
  RetentionCalculationWindowFragment,
  RetentionViewFragment,
} from '../builderFragments';

export const QueryBasicConfigFieldsFragment = gql`
  fragment QueryBasicConfigFields on QueryBasicConfig {
    defaultStart
    defaultEnd
    liveStartDate
    timezone
    dateKinds {
      ...DateKindSupportFields
    }
    logicOps {
      ...LogicalOperatorFields
    }
    cohortConfig {
      ...CohortConfigFields
    }
    defaultDatePicker {
      ...DatePickerFields
    }
  }
  ${CohortConfigFragment}
  ${DateKindSupportFragment}
  ${LogicalOperatorFragment}
  ${DatePickerFieldsFragment}
`;

export const ExpressionQuerySettingFieldsFragment = gql`
  fragment ExpressionQuerySettingFields on ExpressionQuerySetting {
    key
    name
  }
`;

/** Configuration for QUERY mode. */
export const ExpressionQueryConfigFieldsFragment = gql`
  fragment ExpressionQueryConfigFields on ExpressionQueryConfig {
    mode {
      ...ExpressionModeFields
    }
    settings {
      ...ExpressionQuerySettingFields
    }
  }
  ${ExpressionModeFieldsFragment}
  ${ExpressionQuerySettingFieldsFragment}
`;

/** Configuration for HISTOGRAM mode. */
export const ExpressionHistogramConfigFieldsFragment = gql`
  fragment ExpressionHistogramConfigFields on ExpressionHistogramConfig {
    defaultSubject {
      ...SubjectFields
    }
    mode {
      ...ExpressionModeFields
    }
    settings {
      ...ExpressionQuerySettingFields
    }
    subjectBucketKinds
    subjects {
      ...SubjectFields
    }
  }
  ${SubjectFragment}
  ${ExpressionModeFieldsFragment}
  ${ExpressionQuerySettingFieldsFragment}
`;

/** Configuration for IMPACT mode. */
export const ExpressionImpactConfigFieldsFragment = gql`
  fragment ExpressionImpactConfigFields on ExpressionImpactConfig {
    defaultSubject {
      ...SubjectFields
    }
    mode {
      ...ExpressionModeFields
    }
    settings {
      ...ExpressionQuerySettingFields
    }
    subjects {
      ...SubjectFields
    }
  }
  ${SubjectFragment}
  ${ExpressionModeFieldsFragment}
  ${ExpressionQuerySettingFieldsFragment}
`;

export const ExpressionModeConfigsFieldsFragment = gql`
  fragment ExpressionModeConfigsFields on ExpressionModeConfigs {
    histogramConfig {
      ...ExpressionHistogramConfigFields
    }
    impactConfig {
      ...ExpressionImpactConfigFields
    }
    queryConfig {
      ...ExpressionQueryConfigFields
    }
  }
  ${ExpressionHistogramConfigFieldsFragment}
  ${ExpressionImpactConfigFieldsFragment}
  ${ExpressionQueryConfigFieldsFragment}
`;

export const QueryConfigFieldsFragment = gql`
  fragment QueryConfigFields on QueryConfig {
    basicConfig {
      ...QueryBasicConfigFields
    }
    defaultFormulaSamplingRate
    defaultTimeUnit
    measureFunctions {
      ...MeasureFunctionFields
    }
    measureRelOps {
      ...RelationalOperatorFields
    }
    defaultMode {
      ...ExpressionModeFields
    }
    modes {
      ...ExpressionModeFields
    }
    modeConfigs {
      ...ExpressionModeConfigsFields
    }
  }
  ${QueryBasicConfigFieldsFragment}
  ${MeasureFunctionFragment}
  ${RelationalOperatorFragment}
  ${ExpressionModeFieldsFragment}
  ${ExpressionModeConfigsFieldsFragment}
`;

export const FunnelOrderFieldsFragment = gql`
  fragment FunnelOrderFields on FunnelOrder {
    key
    name
  }
`;

export const FunnelQuerySettingFieldsFragment = gql`
  fragment FunnelQuerySettingFields on FunnelQuerySetting {
    key
    name
  }
`;

export const FunnelModeConfigFieldsFragment = gql`
  fragment FunnelModeConfigFields on FunnelModeConfig {
    mode {
      ...FunnelModeFields
    }
    subjects {
      ...SubjectFields
      defaultFunnelOrder {
        ...FunnelOrderFields
      }
      funnelOrders {
        ...FunnelOrderFields
      }
    }
    defaultSubject {
      key
    }
    partitions {
      ...PartitionFields
    }
    defaultPartition {
      ...PartitionFields
    }
    partitionWindowTimeUnits
    defaultPartitionWindowTimeUnit
    defaultPartitionValue {
      ...PartitionValueFields
    }
    timeUnits
    defaultTimeUnit
    orders {
      ...FunnelOrderFields
    }
    defaultOrder {
      ...FunnelOrderFields
    }
  }
  ${FunnelModeFieldsFragment}
  ${FunnelOrderFieldsFragment}
  ${PartitionFieldsFragment}
  ${PartitionValueFieldsFragment}
  ${SubjectFragment}
`;

export const FunnelConfigFieldsFragment = gql`
  fragment FunnelConfigFields on FunnelConfig {
    basicConfig {
      ...QueryBasicConfigFields
    }
    customOrders {
      ...FunnelOrderFields
    }
    defaultCustomOrder {
      ...FunnelOrderFields
    }
    defaultFormulaSamplingRate
    defaultGroupby {
      ...GroupbyFields
    }
    defaultMode {
      ...FunnelModeFields
    }
    groupbys {
      ...GroupbyFields
    }
    modeConfigs {
      ...FunnelModeConfigFields
    }
    modes {
      ...FunnelModeFields
    }
    settings {
      ...FunnelQuerySettingFields
    }
  }
  ${QueryBasicConfigFieldsFragment}
  ${FunnelOrderFieldsFragment}
  ${GroupbyFragment}
  ${FunnelModeFieldsFragment}
  ${FunnelModeConfigFieldsFragment}
  ${FunnelQuerySettingFieldsFragment}
`;

export const DataTableQuerySettingFieldsFragment = gql`
  fragment DataTableQuerySettingFields on DataTableQuerySetting {
    key
    name
  }
`;

export const DataTableConfigFieldsFragment = gql`
  fragment DataTableConfigFields on DataTableConfig {
    basicConfig {
      ...QueryBasicConfigFields
    }
    defaultFormulaSamplingRate
    defaultSubject {
      ...SubjectFields
    }
    defaultTimeUnit
    measureFunctions {
      ...MeasureFunctionFields
    }
    measureRelOps {
      ...RelationalOperatorFields
    }
    subjects {
      ...SubjectFields
    }
    settings {
      ...DataTableQuerySettingFields
    }
  }
  ${QueryBasicConfigFieldsFragment}
  ${SubjectFragment}
  ${MeasureFunctionFragment}
  ${RelationalOperatorFragment}
  ${DataTableQuerySettingFieldsFragment}
`;

export const PathQuerySettingFieldsFragment = gql`
  fragment PathQuerySettingFields on PathQuerySetting {
    key
    name
  }
`;

export const PathConfigFieldsFragment = gql`
  fragment PathConfigFields on PathConfig {
    defaultStart
    defaultEnd
    liveStartDate
    timezone
    logicOps {
      ...LogicalOperatorFields
    }
    subjects {
      ...SubjectFields
    }
    partitions {
      ...PartitionFields
    }
    defaultPartition {
      ...PartitionFields
    }
    defaultSubject {
      ...SubjectFields
    }
    cohortConfig {
      ...CohortConfigFields
    }
    defaultGroupby {
      ...GroupbyFields
    }
    groupbys {
      ...GroupbyFields
    }
    defaultFormulaSamplingRate
    modes {
      key
      name
    }
    defaultMode {
      key
    }
    settings {
      ...PathQuerySettingFields
    }
    defaultDatePicker {
      ...DatePickerFields
    }
  }
  ${LogicalOperatorFragment}
  ${SubjectFragment}
  ${PartitionFieldsFragment}
  ${CohortConfigFragment}
  ${GroupbyFragment}
  ${PathQuerySettingFieldsFragment}
  ${DatePickerFieldsFragment}
`;

export const RetentionQuerySettingFieldsFragment = gql`
  fragment RetentionQuerySettingFields on RetentionQuerySetting {
    key
    name
  }
`;

export const RetentionConfigFieldsFragment = gql`
  fragment RetentionConfigFields on RetentionConfig {
    defaultStart
    defaultEnd
    liveStartDate
    timezone
    cohortUnits
    retentionUnits
    defaultFormulaSamplingRate
    logicOps {
      ...LogicalOperatorFields
    }
    subjects {
      ...SubjectFields
    }
    defaultSubject {
      ...SubjectFields
    }
    cohortConfig {
      ...CohortConfigFields
    }
    calculations {
      ...RetentionCalculationFields
    }
    defaultCalculation {
      ...RetentionCalculationFields
    }
    calculationWindows {
      ...RetentionCalculationWindowFields
    }
    defaultCalculationWindow {
      ...RetentionCalculationWindowFields
    }
    cohortUnits
    retentionUnits
    availableRetentionUnits {
      cohortUnit
      retentionUnits
    }
    defaultAttribution {
      ...AttributionFields
    }
    attributions {
      ...AttributionFields
    }
    defaultGroupby {
      ...GroupbyFields
    }
    groupbys {
      ...GroupbyFields
    }
    retentionViews {
      ...RetentionViewFields
    }
    defaultRetentionView {
      ...RetentionViewFields
    }
    settings {
      ...RetentionQuerySettingFields
    }
    defaultDatePicker {
      ...DatePickerFields
    }
  }
  ${LogicalOperatorFragment}
  ${SubjectFragment}
  ${CohortConfigFragment}
  ${RetentionCalculationFragment}
  ${RetentionCalculationWindowFragment}
  ${AttributionFragment}
  ${GroupbyFragment}
  ${RetentionViewFragment}
  ${RetentionQuerySettingFieldsFragment}
  ${DatePickerFieldsFragment}
`;
